import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";
import {
  FileText,
  GitCompareIcon,
  PlusIcon,
  SearchIcon,
  XIcon,
} from "lucide-react-native";
import React, { memo } from "react";

import LimitedDataWarningBadge from "@/components/molecules/LimitedDataWarningBadge";
import MobileActionMenu from "@/components/molecules/MobileActionMenu";
import { Badge, BadgeText } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import { Heading } from "@/components/atoms/Heading";
import { Image } from "@/components/atoms/Image";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { ifWeb } from "@/utils/platform";

/**
 * Props for PlayerHeaderView component
 */
interface Props {
  knownName: string;
  tidbits: string[];
  roleArchetype: string | null | undefined;
  /** Secondary tactical role archetype, shown as a muted badge after the primary (SE-4844). */
  secondaryRoleArchetype: string | null | undefined;
  valuationStatus: string | null | undefined;
  onLoanFrom: string | null | undefined;
  limitedDataWarning: boolean | null | undefined;
  nationalityFlagUrl: string | null | undefined;
  isInjured: boolean | null | undefined;
  isSimilarDetail?: boolean;
  isSmallScreen: boolean;
  getValuationAction: (
    status: string | null | undefined
  ) => "success" | undefined;
  onSimilar?: () => void;
  onCompare?: () => void;
  onClose?: () => void;
  onAdd?: () => void;
  onAddReport?: () => void;
  onDelete?: () => void;
  isMenuOpen: boolean;
  onMenuOpen: () => void;
  onMenuClose: () => void;
  onAddToList: () => void;
  onAddReportFromMenu: () => void;
}

/**
 * Pure view component for player header
 * @param root0 - Component props
 * @param root0.nationalityFlagUrl - Player nationality flag SVG image URL
 * @param root0.isInjured - Whether player is injured
 * @param root0.knownName - Player display name
 * @param root0.tidbits - Array of player info tidbits
 * @param root0.roleArchetype - Player role archetype
 * @param root0.secondaryRoleArchetype - Player secondary role archetype, shown as a muted badge after the primary (SE-4844)
 * @param root0.valuationStatus - Player valuation status
 * @param root0.onLoanFrom - Team player is on loan from
 * @param root0.limitedDataWarning - Whether to show limited data warning
 * @param root0.isSimilarDetail - Whether in similar player detail view
 * @param root0.isSmallScreen - Whether device has small screen
 * @param root0.getValuationAction - Function to get valuation badge action
 * @param root0.onAdd - Handler for add button
 * @param root0.onAddReport - Handler for add report
 * @param root0.onClose - Handler for close button
 * @param root0.onCompare - Handler for compare button
 * @param root0.onSimilar - Handler for similar button
 * @param root0.isMenuOpen - Whether menu is open
 * @param root0.onMenuOpen - Handler for menu open
 * @param root0.onMenuClose - Handler for menu close
 * @param root0.onAddToList - Handler for add to list menu item
 * @param root0.onAddReportFromMenu - Handler for add report menu item
 */
const PlayerHeaderView = ({
  nationalityFlagUrl,
  isInjured,
  knownName,
  tidbits,
  roleArchetype,
  secondaryRoleArchetype,
  valuationStatus,
  onLoanFrom,
  limitedDataWarning,
  isSimilarDetail,
  isSmallScreen,
  getValuationAction,
  onAdd,
  onAddReport,
  onClose,
  onCompare,
  onSimilar,
  isMenuOpen,
  onMenuOpen,
  onMenuClose,
  onAddToList,
  onAddReportFromMenu,
}: Props) => (
  <Stack className="web:cursor-pointer w-full gap-2">
    <Stack
      direction="horizontal"
      className="items-center justify-between gap-3"
    >
      <Heading
        variant="display-sm"
        className={`shrink native:py-4 ${isSmallScreen ? "flex-1" : ""} text-content-primary`}
      >
        {knownName}
      </Heading>
      <Stack direction="horizontal" className="items-center gap-1">
        {onAdd && (
          <>
            <Button
              testID={TEST_ID_PLAYER_DETAIL.ADD_TO_LIST_BUTTON}
              size="lg"
              variant="link"
              className="rounded-none px-3 py-2"
              onPress={onMenuOpen}
              accessible
              accessibilityLabel="Add player"
              accessibilityRole="button"
              icon={PlusIcon}
            />

            <MobileActionMenu
              isOpen={isMenuOpen}
              onClose={onMenuClose}
              testID="header-player-action-menu"
              items={[
                {
                  icon: PlusIcon,
                  label: "Add to List",
                  onPress: onAddToList,
                },
                ...(onAddReport
                  ? [
                      {
                        icon: FileText,
                        label: "Add Report",
                        onPress: onAddReportFromMenu,
                      },
                    ]
                  : []),
              ]}
            />
          </>
        )}
        {onSimilar && (
          <Button
            testID={TEST_ID_PLAYER_DETAIL.SIMILAR_PLAYER_BUTTON}
            size="lg"
            variant="link"
            className="rounded-none px-3 py-2"
            onPress={onSimilar}
            accessible
            accessibilityLabel="Find similar players"
            accessibilityRole="button"
            icon={SearchIcon}
            iconSize="lg"
          />
        )}
        {onCompare && !isSimilarDetail && (
          <Button
            testID={TEST_ID_PLAYER_DETAIL.COMPARE_BUTTON}
            size="lg"
            variant="link"
            className="overflow-visible rounded-none px-3 py-2"
            onPress={onCompare}
            accessible
            accessibilityLabel="Compare players"
            accessibilityRole="button"
            icon={GitCompareIcon}
            iconSize="lg"
          />
        )}
        {onClose && (
          <Button
            testID={TEST_ID_PLAYER_DETAIL.CLOSE_BUTTON}
            size="lg"
            variant="link"
            className="rounded-none px-3 py-2"
            onPress={onClose}
            accessible
            accessibilityLabel="Close player details"
            accessibilityRole="button"
            icon={XIcon}
            iconTone="secondary"
          />
        )}
      </Stack>
    </Stack>
    <Stack direction="horizontal" className="flex-wrap items-center gap-2">
      {tidbits.map((tidbit, i) => (
        <React.Fragment key={tidbit}>
          <Text variant={ifWeb("title-sm", "title-lg")}>{tidbit}</Text>
          {i < tidbits.length - 1 && <Text variant="unstyled">&middot;</Text>}
        </React.Fragment>
      ))}
      {tidbits.length > 0 && (
        <>
          {nationalityFlagUrl != null && (
            <>
              <Text variant="unstyled">&middot;</Text>
              <Image
                source={{ uri: nationalityFlagUrl }}
                className="h-4 w-6"
                alt="Player nationality flag"
                accessibilityLabel="Player nationality flag"
              />
            </>
          )}
          {isInjured && (
            <>
              <Text variant="unstyled">&middot;</Text>
              <Image
                source={require("../../../../assets/icons/ic_injured_flag.png")}
                className="h-3 w-3"
                alt="Injured"
              />
            </>
          )}
        </>
      )}
    </Stack>
    <Stack
      direction="horizontal"
      className={`${!isSmallScreen ? "" : "pb-4"} items-start gap-3`}
    >
      <Stack direction="horizontal" className="flex-wrap items-center gap-2">
        {Boolean(roleArchetype) && (
          <Badge size="lg" variant="solid" action="info">
            <BadgeText
              {...(!isSmallScreen ? { className: "font-heading" } : {})}
            >
              {roleArchetype}
            </BadgeText>
          </Badge>
        )}
        {Boolean(secondaryRoleArchetype) && (
          <Badge size="lg" variant="solid" action="muted">
            <BadgeText
              {...(!isSmallScreen ? { className: "font-heading" } : {})}
            >
              {secondaryRoleArchetype}
            </BadgeText>
          </Badge>
        )}
        {Boolean(valuationStatus) && getValuationAction(valuationStatus) && (
          <Badge
            size="lg"
            variant="solid"
            action={getValuationAction(valuationStatus)}
          >
            <BadgeText
              {...(!isSmallScreen ? { className: "font-heading" } : {})}
            >
              {valuationStatus}
            </BadgeText>
          </Badge>
        )}
        {Boolean(onLoanFrom) && (
          <Badge size="lg" variant="solid" action="warning">
            <BadgeText
              {...(!isSmallScreen ? { className: "font-heading" } : {})}
            >
              On Loan from {onLoanFrom}
            </BadgeText>
          </Badge>
        )}
        <LimitedDataWarningBadge
          show={!!limitedDataWarning}
          isSmallScreen={isSmallScreen}
        />
      </Stack>
    </Stack>
  </Stack>
);

PlayerHeaderView.displayName = "PlayerHeaderView";

export default memo(PlayerHeaderView);
